/**
 * @file 開いているページに付く音声を鳴らす。
 *
 * @what: 開いているページの音声が前のページと違うものになったときだけ、頭から鳴らし直す。
 *   同じ音声が付く次のページへ送ったときは鳴らしたまま続ける。音声を持たないページへ
 *   送ったら止める。「自動で再生」が切のときは、取得だけして鳴らさない。
 * @why: 紙面は見開き2ページで1つのまとまりになっていて、音声もそのまとまりに1本付く。
 *   ページごとに鳴らし直すと、右のページへ送った拍子に頭へ戻ってしまう (代表の指摘、
 *   2026-09-13)。同じ音声を指しているあいだは1つの続きとして扱う。
 * @failure: 取得に失敗したら status "error" (そのページだけ。送り直せば取り直す)。
 *   ブラウザが操作なしの再生を止めたときは status "blocked" にして、押して始められる
 *   ようにする。※なぜなら iOS を含むブラウザは、利用者の操作を起点にしない音を鳴らさない
 *   ためである。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { loadPageAudio } from "../lib/pageAudioCache";

export type PageAudioStatus = "none" | "loading" | "playing" | "paused" | "blocked" | "error";

export interface PageAudio {
  status: PageAudioStatus;
  /** 鳴らす (止まっているとき)。取得の途中なら、届いてから鳴る。 */
  play: () => void;
  /** 止める (次に押すと頭から鳴る)。 */
  stop: () => void;
}

export function usePageAudio(
  /** 開いているページに付く音声 (無ければ null)。同じ値のあいだは鳴らし直さない。 */
  audioPath: string | null,
  contentType: string,
  autoplay: boolean,
): PageAudio {
  const [status, setStatus] = useState<PageAudioStatus>("none");
  const elRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  // 取得が終わる前に「再生」を押されたら、届いた時点で鳴らす。
  const wantPlayRef = useRef(false);

  // 音の出し手は1つだけ作り、ページを送っても作り直さない (端末の同時再生数を使わない)。
  //
  // @why: 作るのは効果の中に置く。※なぜなら描画の途中で作ると、React が開発時に行う
  //   二重マウントで出し手が2つでき、先に鳴り始めた方が後から作られた方に潰されて
  //   0.5秒ほどで止まるためである。
  const ensureEl = useCallback((): HTMLAudioElement | null => {
    if (elRef.current === null && typeof Audio !== "undefined") {
      elRef.current = new Audio();
    }
    return elRef.current;
  }, []);

  const startPlayback = useCallback(() => {
    const el = elRef.current;
    if (!el || urlRef.current === null) return;
    el.currentTime = 0;
    el.play().then(
      () => setStatus("playing"),
      () => setStatus("blocked"),
    );
  }, []);

  useEffect(() => {
    const el = ensureEl();
    if (!el) return;
    const onEnded = () => setStatus("paused");
    const onPause = () => setStatus((s) => (s === "playing" ? "paused" : s));
    el.addEventListener("ended", onEnded);
    el.addEventListener("pause", onPause);
    return () => {
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("pause", onPause);
    };
  }, [ensureEl]);

  // 画面を離れるときは鳴らしたままにしない。出し手も捨て、開き直したら作り直す。
  useEffect(() => {
    return () => {
      const el = elRef.current;
      if (!el) return;
      el.pause();
      el.removeAttribute("src");
      elRef.current = null;
      urlRef.current = null;
    };
  }, []);

  useEffect(() => {
    const el = ensureEl();
    if (!el) return;
    el.pause();
    urlRef.current = null;
    wantPlayRef.current = autoplay;
    if (!audioPath) {
      el.removeAttribute("src");
      setStatus("none");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    try {
      loadPageAudio(audioPath, contentType).then(
        (url) => {
          if (cancelled) return;
          urlRef.current = url;
          el.src = url;
          if (wantPlayRef.current) startPlayback();
          else setStatus("paused");
        },
        (err: unknown) => {
          if (cancelled) return;
          console.error("[study] failed to load page audio", err);
          setStatus("error");
        },
      );
    } catch (err) {
      console.error("[study] failed to load page audio", err);
      setStatus("error");
    }
    return () => {
      cancelled = true;
    };
    // 依存は音声そのものだけにする。※なぜなら見開きの右のページへ送ってもこの効果を
    //   走らせないことが、鳴らしたまま続けるということだからである。
    // autoplay は「その音声に変わった時点の値」を使う (途中で入に変えても鳴らし直さない)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioPath, contentType, startPlayback, ensureEl]);

  const play = useCallback(() => {
    wantPlayRef.current = true;
    if (urlRef.current === null) return;
    startPlayback();
  }, [startPlayback]);

  const stop = useCallback(() => {
    wantPlayRef.current = false;
    elRef.current?.pause();
    setStatus((s) => (s === "loading" ? "loading" : s === "none" ? "none" : "paused"));
  }, []);

  return { status, play, stop };
}
